import { Controller } from "@hotwired/stimulus"
import Honeybadger from "@honeybadger-io/js"

/**
 * Unified controller for cell-level interactions in Plantafel sections
 * Handles "+" button clicks to create new elements
 *
 * Note: Hover detection is handled by plantafel_section_controller.js
 * which uses mousemove on the section to determine which cell is hovered.
 *
 * Replaces:
 * - plantafel_appointment_cell_controller.js
 * - plantafel_sick_note_cell_controller.js
 * - plantafel_absence_drop_controller.js
 */
export default class extends Controller {
  static targets = ["addOverlay"]
  static values = {
    sectionType: String, // 'appointments', 'sick_notes', 'leave_requests'
    date: String,
    empty: Boolean,
    newPath: String, // Pre-built path from the server
  }

  connect() {
    // Cell is ready - hover detection is handled by section controller
  }

  disconnect() {
    // Cleanup if needed
  }

  /**
   * Opens the new element form in sidedrawer
   * Called when clicking the "+" button
   */
  openNewForm(event) {
    event.preventDefault()
    event.stopPropagation()

    const url = this.newPathValue || this.buildNewPath()

    if (!url) {
      Honeybadger.notify("Missing URL for new element form", {
        context: { level: "warning", component: "plantafel-cell" },
      })
      return
    }

    this.fetchForm(url)
  }

  /**
   * Builds the URL for the new element form based on section type
   */
  buildNewPath() {
    const date = this.dateValue
    if (!date) return null

    switch (this.sectionTypeValue) {
      case "appointments":
        return `/appointments/new?start_date=${date}&source=plantafel`
      case "sick_notes":
        return `/sick_notes/new?date_from=${date}&date_to=${date}&source=plantafel`
      case "leave_requests":
        return `/leave_requests/new?date_from=${date}&date_to=${date}&source=plantafel`
      default:
        Honeybadger.notify(`Unknown plantafel section type: ${this.sectionTypeValue}`, {
          context: { level: "warning", component: "plantafel-cell" },
        })
        return null
    }
  }

  /**
   * Fetches the form via Turbo Stream and renders in sidedrawer
   */
  fetchForm(url) {
    const csrfToken = document.head
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content")

    fetch(url, {
      method: "GET",
      credentials: "same-origin",
      headers: {
        Accept: "text/vnd.turbo-stream.html",
        "X-CSRF-Token": csrfToken,
        "X-Requested-With": "XMLHttpRequest",
      },
    })
      .then((response) => response.text())
      .then((html) => {
        Turbo.renderStreamMessage(html)
      })
      .catch((error) => {
        Honeybadger.notify(error, {
          context: { component: "plantafel-cell", action: "fetch_form" },
        })
        // Fallback to regular navigation
        window.location.href = url
      })
  }
}
